import React, { useState, forwardRef, useRef } from 'react';
import { Box, Typography, Button, Divider, Collapse } from '@mui/material';
import LoadingDots from '../LoadingDots/LoadingDots';
import RollingNumber from '../Loaders/RollingNumber';
import SkeletonLoaderWrapper from '../Loaders/SkeletonLoaderWrapper';

const LinkIcon = 'https://assets.askhippo.co.za/Icons/common/Union_Black.svg';
const BuyIconPink = 'https://assets.askhippo.co.za/Icons/common/Union_Pink.svg';

const infoRows = [
  { label: 'Waiting Periods', key: 'WaitingPeriod' },
  { label: 'Co - Payments', key: 'CoPayments' },
  { label: 'Annual Limit', key: 'AnnualLimit' },
  { label: 'Casualty Ward', key: 'CasualtyWard' },
  { label: 'Hospital Exclusions', key: 'HospitalExclusions' },
];

const renderValueWithBoldCurrency = (text) => {
  if (!text) return null;

  const str = String(text);
  // Match SA Rand currency patterns like "R210 580" or "R22,000"
  const match = str.match(/(R\s?\d[\d\s,.,]*)/i); // removed unnecessary escape for dot

  if (match) {
    const amount = match[1];
    const parts = str.split(amount);
    return (
      <>
        {parts[0]}
        <Box component="span" sx={{ fontWeight: 700 }}>
          {amount}
        </Box>
        {parts[1]}
      </>
    );
  }

  return str;
};

const HDivider = (props) => (
  <Divider
    sx={{
      borderColor: '#E0E0E0',
      mx: '16px',
    }}
    {...props}
  />
);

const GridVDivider = ({ sx = {} }) => (
  <Box
    sx={{
      width: '1px',
      backgroundColor: '#E0E0E0',
      alignSelf: 'stretch', // fill grid row height
      minHeight: '1px',
      ...sx,
    }}
  />
);

const ProviderTileComponent_MedicalGap = forwardRef(
  (
    {
      data,
      initialLoadDone,
      onSelectQuote,
      onBuyOnline,
      buyNowLoading,
      buyNowLoadingForQuotePk,
      bestDealContentReady = true,
      wasLoadingOnMount = useRef(!bestDealContentReady),
    },
    ref
  ) => {
    const { CompanyName, LogoURL, TotalPremium } = data;

    const [isExpanded, setIsExpanded] = useState(false);

    const isBestDealLoading = data.bestDeal && !bestDealContentReady;
    const isBest = Boolean(data.bestDeal && initialLoadDone);
    const hasBuyNow = Boolean(data.HasBuyOnline || data.OnlinePremium);

    let derivedShortUSP = '';

    if (data.CoverType === 'comprehensive') {
      derivedShortUSP = `Comprehensive Gap Cover`;
    } else if (data.CoverType === 'basic') {
      derivedShortUSP = `Basic Gap Cover`;
    } else if (data.CoverType === 'intermediate') {
      derivedShortUSP = `Intermediate Gap Cover`;
    } else {
      derivedShortUSP = 'Gap Cover';
    }

    const handleSelectQuote = (evt) => {
      onSelectQuote(evt, { action: 'select-quote', data });
    };

    const handleBuyOnline = (evt) => {
      onBuyOnline(evt, { action: 'buy-online', data });
    };

    return (
      <Box
        ref={ref}
        data-testid="provider-tile"
        sx={{
          width: '909px',
          minHeight: data.bestDeal && initialLoadDone ? '236px' : '0px',
          borderRadius: '22px',
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 5px 15px 0px #00000026',
          border:
            data.bestDeal && initialLoadDone ? '2px solid #2DC8CB' : 'none',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* ===== 1) Best Deal banner (own group) ===== */}
        {data.bestDeal && initialLoadDone && (
          <Box
            sx={{
              backgroundColor: '#2DC8CB',
              borderTopLeftRadius: '18px',
              borderTopRightRadius: '18px',
              px: '18px',
              py: '12px',
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize: '1.250rem',
                lineHeight: '120%',
                letterSpacing: '-0.03em',
                color: '#FFFFFF',
              }}
            >
              {bestDealContentReady ? (
                'Your Best Deal!'
              ) : (
                <>
                  Calculating Your Best Deal <LoadingDots variant="buy-now" />
                </>
              )}
            </Typography>
          </Box>
        )}

        {/* ===== 2) Main row group: Logo | | Content | | Premium/Actions ===== */}
        <Box
          sx={{
            // no side padding here; we model 16px as real columns
            display: 'grid',
            // Columns (from left):
            // 16 (gutter) | 210 (logo) | 16 (gutter) | 1 (VD #1 @ 242) | 17 (gutter)
            // 291.5 (content) | 15 (gutter) | 1 (VD #2 @ 551.5) | 16 (gutter) | auto (premium)
            gridTemplateColumns:
              '16px 210px 16px 1px 17px 277px 15px 1px 16px auto',
            alignItems: 'stretch',
            py: data.bestDeal && initialLoadDone ? '12px' : '16px',
          }}
        >
          {/* left gutter */}
          <Box />

          {/* Logo */}
          <Box
            sx={{
              minHeight: '100px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: '12px',
            }}
          >
            <SkeletonLoaderWrapper loading={isBestDealLoading}>
              <img
                src={LogoURL}
                alt={CompanyName}
                style={{
                  maxWidth: '100%',
                  maxHeight: '76px',
                  objectFit: 'contain',
                }}
              />
            </SkeletonLoaderWrapper>
          </Box>

          {/* gutter before VD #1 */}
          <Box />

          {/* VD #1 at 242px from left */}
          <GridVDivider />

          {/* gutter after VD #1 */}
          <Box />

          {/* Content (name, usp, stars, bullets) */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: '6px',
              // optional internal padding if you want the old “content starts at 259px” feel:
              // (we already modeled that with the 17px gutter, so usually not needed)
            }}
          >
            <SkeletonLoaderWrapper loading={isBestDealLoading}>
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  lineHeight: '24px',
                  color: '#1E354E',
                }}
              >
                {CompanyName}
              </Typography>
            </SkeletonLoaderWrapper>
            <SkeletonLoaderWrapper loading={isBestDealLoading}>
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: '1rem',
                  lineHeight: '28px',
                  mt: '-6px',
                  color: '#738289',
                }}
              >
                {derivedShortUSP}
              </Typography>
            </SkeletonLoaderWrapper>

            {data.bestDeal && initialLoadDone && (
              <>
                <SkeletonLoaderWrapper loading={isBestDealLoading}>
                  <Box sx={{ display: 'flex', mt: 0.5 }}></Box>

                  <Box
                    sx={{
                      mt: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Inter',
                        fontWeight: 400,
                        fontSize: '0.875rem',
                        lineHeight: '1.275rem',
                        color: '#738289',
                      }}
                    >
                      Medical Scheme Tariff:{' '}
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        {data?.MedicalSchemeTarrif != null
                          ? data.MedicalSchemeTarrif
                          : '0'}
                        %
                      </Box>
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      mt: -1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Inter',
                        fontWeight: 400,
                        fontSize: '0.875rem',
                        lineHeight: '1.275rem',
                        color: '#738289',
                      }}
                    >
                      Annual Limit:{' '}
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        {data?.AnnualLimit
                          ? data.AnnualLimit.split(' ')[0] + // gets "R210"
                            ' ' +
                            data.AnnualLimit.split(' ')[1] // gets "580"
                          : 'R0'}
                      </Box>
                    </Typography>
                  </Box>
                </SkeletonLoaderWrapper>
              </>
            )}
          </Box>

          {/* gutter before VD #2 */}
          <Box />

          {/* VD #2 at 551.5px from left */}
          <GridVDivider />

          {/* gutter after VD #2 */}
          <Box />

          {/* Premium / actions */}
          {/* Premium column (chips fixed at top, price/excess vertically centered) */}
          <Box
            sx={{
              position: 'relative',
              minHeight: '100px',
              mb: data.bestDeal && initialLoadDone ? '60px' : 0,
              height: '100%',
            }}
          >
            {/* Chips: fixed at the top, not affecting layout/centering */}
            {Array.isArray(data.RetailChips) && data.RetailChips.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  pointerEvents: 'none', // chips are informational; keep clicks for CTA on the right
                }}
              >
                {data.RetailChips.map((chip, idx) => (
                  <Box key={idx}>
                    <SkeletonLoaderWrapper loading={isBestDealLoading}>
                      <Box
                        key={idx}
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: '#162F3B',
                          backgroundColor: chip.color,
                          letterSpacing: '-0.02em',
                          lineHeight: '1.125rem',
                          borderRadius: '6px',
                          px: '8px',
                          py: '2px',
                          display: 'inline-block',
                        }}
                      >
                        {chip.text}
                      </Box>
                    </SkeletonLoaderWrapper>
                  </Box>
                ))}
              </Box>
            )}

            {/* Centering wrapper: vertically centers the price/excess block */}
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '4px',
                  mt: data.bestDeal && initialLoadDone ? '-40px' : '0px',
                }}
              >
                <Typography
                  sx={{
                    mt: '8px',
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    fontSize: '1rem',
                    color: '#A4ACB0',
                  }}
                >
                  From
                </Typography>
                <Box
                  sx={{ display: 'flex', alignItems: 'baseline', mt: '-12px' }}
                >
                  <RollingNumber
                    value={TotalPremium}
                    prefix={{
                      text: 'R',
                    }}
                    digitSx={{
                      fontFamily: 'Inter',
                      fontWeight: 700,
                      fontSize: '3rem',
                      letterSpacing: '-0.02em',
                      color: '#1E354E',
                    }}
                    suffix={{
                      text: 'pm',
                      sx: {
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        ml: '2px',
                        color: '#1E354E',
                      },
                    }}
                    wrapperSx={{
                      mt: '2px',
                    }}
                    extraLoops={3}
                    playSignal={
                      data.bestDeal && wasLoadingOnMount.current ? 1 : 0
                    }
                    animate={data.bestDeal && wasLoadingOnMount.current}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        {/* Fixed-position CTA (right side) */}
        {hasBuyNow ? (
          /* Two CTAs — Buy Now + I'm Interested */
          <Box
            sx={{
              position: 'absolute',
              right: '16px',
              top: isBest ? '175px' : '76px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              zIndex: 5,
            }}
          >
            {/* LEFT: Buy Now */}
            <Button
              data-testid="quote-cta-buy"
              onClick={handleBuyOnline}
              disabled={buyNowLoading}
              sx={{
                borderRadius: '18px',
                px: isBest ? '16px' : '12px',
                py: '8px',
                backgroundColor: '#FFFFFF',
                border: '1px solid #FF0074',
                boxShadow: '0px 1px 2px 0px #1018280D',
                textTransform: 'none',
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { backgroundColor: '#f4eeee' },
              }}
            >
              {buyNowLoading && buyNowLoadingForQuotePk === data.QuotePK ? (
                <LoadingDots variant="buy-now-pink" />
              ) : (
                <>
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 600,
                      fontSize: '1rem',
                      color: '#FF0074',
                      mr: '4px',
                    }}
                  >
                    {isBest ? 'Buy Now' : 'Buy'}
                  </Typography>
                  <Box
                    component="img"
                    src={BuyIconPink}
                    alt="Buy Now"
                    sx={{ mx: '4px', mb: '2px', width: '18px', height: '18px' }}
                  />
                </>
              )}
            </Button>

            {/* RIGHT: I'm Interested */}
            <Button
              data-testid="quote-cta-select"
              onClick={handleSelectQuote}
              disabled={(isBest && isBestDealLoading) || buyNowLoading}
              sx={{
                borderRadius: '18px',
                px: isBest ? '16px' : '12px',
                py: '8px',
                backgroundColor: '#FF0074',
                border: '1px solid #FF0074',
                boxShadow: '0px 1px 2px 0px #1018280D',
                textTransform: 'none',
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { backgroundColor: '#e60067' },
                ...(isBest &&
                  isBestDealLoading && {
                    backgroundColor: '#D5D5D5 !important',
                    borderColor: '#D5D5D5 !important',
                    color: '#7A7A7A !important',
                    cursor: 'not-allowed',
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: '#D5D5D5 !important' },
                  }),
              }}
            >
              {isBest ? (
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    fontSize: '1rem',
                    lineHeight: '24px',
                    letterSpacing: '-0.02em',
                    color: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  I&apos;m Interested
                  <span
                    className="material-symbols-rounded"
                    style={{ fontSize: '1.5rem', color: '#FFFFFF' }}
                  >
                    double_arrow
                  </span>
                </Typography>
              ) : (
                <span
                  className="material-symbols-rounded"
                  style={{ fontSize: '1.5rem', color: '#FFFFFF' }}
                >
                  double_arrow
                </span>
              )}
            </Button>
          </Box>
        ) : isBest ? (
          /* Single CTA — best deal, no buy-now available */
          <Button
            data-testid="quote-cta-select"
            onClick={handleSelectQuote}
            disabled={isBestDealLoading}
            sx={{
              position: 'absolute',
              right: '16px',
              top: '175px',
              borderRadius: '18px',
              gap: '8px',
              px: '16px',
              py: '8px',
              backgroundColor: '#FF0074',
              border: '1px solid #FF0074',
              boxShadow: '0px 1px 2px 0px #1018280D',
              textTransform: 'none',
              minWidth: 0,
              '&:hover': { backgroundColor: '#e60067' },
              ...(isBestDealLoading && {
                backgroundColor: '#D5D5D5 !important',
                borderColor: '#D5D5D5 !important',
                color: '#7A7A7A !important',
                cursor: 'not-allowed',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#D5D5D5 !important',
                },
              }),
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: '1rem',
                lineHeight: '24px',
                letterSpacing: '-0.02em',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              I&apos;m Interested
              <span
                className="material-symbols-rounded"
                style={{ fontSize: '1.5rem', color: '#FFFFFF' }}
              >
                double_arrow
              </span>
            </Typography>
          </Button>
        ) : (
          /* Single CTA — not best deal, no buy-now available */
          <Button
            data-testid="quote-cta-select"
            onClick={handleSelectQuote}
            sx={{
              position: 'absolute',
              right: '16px',
              top: '76px',
              backgroundColor: '#FF0074',
              borderRadius: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textTransform: 'none',
              minWidth: 0,
              px: '16px',
              py: '8px',
              boxSizing: 'border-box',
              '&:hover': { backgroundColor: '#e60067' },
            }}
          >
            <span
              className="material-symbols-rounded"
              style={{ fontSize: '1.5rem', color: '#FFFFFF' }}
            >
              double_arrow
            </span>
          </Button>
        )}

        {/* Fixed-position Expand/Collapse */}
        <Button
          onClick={() => setIsExpanded((p) => !p)}
          sx={{
            position: 'absolute',
            top: `${data.bestDeal && initialLoadDone ? 67 : 16}px`,
            right: '16px',
            width: '48px',
            height: '48px',
            p: '8px',
            minWidth: 0,
            backgroundColor: 'transparent',
            boxShadow: 'none',
            '&:hover': { backgroundColor: 'transparent' },
          }}
          aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
        >
          <span
            className="material-symbols-rounded"
            style={{
              fontWeight: 500,
              fontSize: '1.5rem',
              color: '#000',
              transform: isExpanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.3s ease',
              display: 'inline-block',
            }}
          >
            keyboard_arrow_down
          </span>
        </Button>
        <Collapse
          in={isExpanded}
          timeout={{ enter: 300, exit: 300 }}
          unmountOnExit
        >
          {/* ===== 3) Divider under main row ===== */}

          <HDivider
            sx={{
              mt: '16px',
            }}
          />

          {/* ===== 4) Description group (full width) ===== */}
          <Box
            sx={{
              pl: '32px',
              py: '16px',
              pr: '32px',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: '16px',
            }}
          >
            {/* Left: heading + description */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  mb: 1,
                  color: '#1E354E',
                }}
              >
                Description
              </Typography>

              {/* Wrap description + button so they align */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '0.875rem',
                    color: '#1E354E',
                    maxWidth: '707px',
                    width: '100%',
                    wordWrap: 'break-word',
                    pr: '16px',
                  }}
                >
                  {data.Description}
                </Typography>

                {/* Right: button (only if ReadMoreURL exists & non-empty) */}
                {data?.ReadMoreURL &&
                  String(data.ReadMoreURL).trim() !== '' && (
                    <Button
                      component="a"
                      href={data.ReadMoreURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="contained"
                      sx={{
                        position: 'absolute',
                        right: '16px',
                        backgroundColor: '#E3E6E8',
                        borderRadius: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textTransform: 'none',
                        minWidth: 0,
                        px: '16px',
                        py: '8px',
                        boxSizing: 'border-box',
                        '&:hover': { backgroundColor: '#d1d1d1' },
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Inter',
                          fontWeight: 600,
                          px: 0,
                          fontSize: '1rem',
                          mr: '4px',
                        }}
                      >
                        View Details
                      </Typography>
                      <Box
                        component="img"
                        src={LinkIcon}
                        alt="Link"
                        sx={{
                          mx: '4px',
                          mb: '2px',
                          width: '18px',
                          height: '18px',
                        }}
                      />
                    </Button>
                  )}
              </Box>
            </Box>
          </Box>

          {/* ===== 5) Horizontal divider ===== */}
          <HDivider />

          {/* ===== 6) Bottom group: Benefits/Features | | Reviews ===== */}
          <Box sx={{ pl: '32px', py: '16px' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1px 24px',
                alignItems: 'stretch',
                mb: '16px',
              }}
            >
              <Box sx={{ pr: '16px' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    rowGap: 1.5,
                  }}
                >
                  {infoRows
                    .map(({ label, key }) => ({
                      label,
                      value: data && data[key],
                    }))
                    .filter(
                      ({ value }) =>
                        value != null && String(value).trim() !== ''
                    )
                    .map(({ label, value }) => (
                      <Box
                        key={label}
                        sx={{
                          backgroundColor: '#F6F6F6',
                          border: '1px solid #E1E1E1',
                          borderRadius: '12px',
                          width: '100%',
                          maxWidth: '869px',
                          minHeight: '52px',
                          display: 'grid',
                          gridTemplateColumns: '180px 1fr', // fixed header column width
                          alignItems: 'center',
                          columnGap: '32px',
                          px: '24px',
                          py: '16px',
                          boxSizing: 'border-box',
                        }}
                      >
                        {/* Header column */}
                        <Typography
                          sx={{
                            fontFamily: 'Inter',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            lineHeight: '1.275rem',
                            color: '#162F3B',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {label}
                        </Typography>

                        {/* Value column with bolded currency */}
                        <Typography
                          sx={{
                            fontFamily: 'Inter',
                            fontWeight: 400,
                            fontSize: '0.875rem',
                            lineHeight: '1.275rem',
                            color: '#162F3B',
                            textAlign: 'left',
                          }}
                        >
                          {renderValueWithBoldCurrency(value)}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              </Box>

              <Box />
            </Box>
          </Box>

          {/* ===== 7) Footer group: FSP info (optional) ===== */}
          {data.FSPLine && (
            <>
              {/* <HDivider /> */}
              <Box sx={{ px: '16px', py: '12px' }}>
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    fontSize: '0.875rem',
                    lineHeight: '140%',
                    color: '#B3B3B3',
                    mt: '-4px',
                    mx: '16px',
                    mb: '8px',
                  }}
                >
                  {data.FSPLine}
                </Typography>
              </Box>
            </>
          )}
        </Collapse>
      </Box>
    );
  }
);

ProviderTileComponent_MedicalGap.displayName =
  'ProviderTileComponent_MedicalGap';
export default ProviderTileComponent_MedicalGap;
